// Reusable bits used across club + platform + sponsor screens
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Bell, Search } from "lucide-react";
import { useAuth, applyTenantTheme } from "../lib/auth";
import { SouburWordmark, SoubourMark } from "./brand/SoubourMark";

export const Crest = ({ tenant, size = 36, className = "" }) => {
    if (!tenant) return null;
    const logoPath = tenant.brand?.logo_path;
    const initials = (tenant.brand?.crest_initials || tenant.short_name || tenant.name || "FC").slice(0, 2).toUpperCase();
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    if (logoPath) {
        return (
            <div
                className={`overflow-hidden border hairline shrink-0 ${className}`}
                style={{ width: size, height: size, background: "#0a0a0a" }}
                data-testid="tenant-crest"
            >
                <img
                    src={`${apiBase}/api/files/${logoPath}`}
                    alt={tenant.name}
                    className="w-full h-full object-contain"
                    style={{ display: "block" }}
                />
            </div>
        );
    }
    return (
        <div
            className={`flex items-center justify-center font-display font-extrabold tracking-tight shrink-0 ${className}`}
            style={{
                background: tenant.brand?.primary || "#F59E0B",
                color: tenant.brand?.primary_foreground || "#000000",
                width: size,
                height: size,
                fontSize: size * 0.36,
            }}
            data-testid="tenant-crest"
        >
            {initials}
        </div>
    );
};

export const TopBar = ({ breadcrumb, right }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const onLogout = async () => {
        await logout();
        navigate("/login");
    };
    const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
    return (
        <div className="border-b hairline px-6 md:px-10 py-4 flex items-center justify-between gap-4 sticky top-0 bg-[rgba(5,5,5,0.85)] backdrop-blur-xl z-30">
            <div className="flex items-center gap-4 flex-1 min-w-0 text-xs text-zinc-500">{breadcrumb}</div>
            <div className="flex items-center gap-3">
                {right}
                <button className="border hairline p-2.5 hover:bg-white/5" data-testid="topbar-notifications" aria-label="Notifications">
                    <Bell size={14} />
                </button>
                <div className="hidden md:flex items-center gap-2.5 border hairline px-3 py-1.5">
                    <div className="w-7 h-7 bg-white/10 flex items-center justify-center text-[10px] font-bold tracking-tight" data-testid="topbar-user-avatar">{initials}</div>
                    <div className="leading-tight">
                        <div className="text-xs text-white">{user?.name}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-ultra-wide">{user?.role?.replace("_", " ")}</div>
                    </div>
                    <button onClick={onLogout} className="ml-2 text-zinc-500 hover:text-white" data-testid="topbar-logout" aria-label="Sign out">
                        <LogOut size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export const TenantThemer = ({ tenant }) => {
    useEffect(() => {
        applyTenantTheme(tenant);
        return () => applyTenantTheme(null);
    }, [tenant]);
    return null;
};

export const Pill = ({ children, tone = "neutral", testId }) => {
    const tones = {
        neutral: "border-white/15 text-zinc-300",
        active: "border-emerald-500/40 text-emerald-300",
        pending: "border-amber-500/40 text-amber-300",
        paused: "border-zinc-600 text-zinc-500",
        cancelled: "border-zinc-700 text-zinc-600",
        unpaid: "border-rose-500/40 text-rose-300",
        paid: "border-emerald-500/40 text-emerald-300",
    };
    return (
        <span data-testid={testId} className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-ultra-wide ${tones[tone] || tones.neutral}`}>
            {children}
        </span>
    );
};

export const KpiCard = ({ label, value, sub, deltaPct, testId, accent = false, children }) => (
    <div data-testid={testId}
        className={`border hairline p-6 hover:border-white/20 transition-colors flex flex-col justify-between min-h-[140px] ${accent ? "bg-[rgba(245,158,11,0.04)]" : ""}`}>
        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</div>
        <div>
            <div className="font-display font-extrabold text-4xl tracking-tightest text-white mt-3 leading-none">{value}</div>
            {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
            {children}
        </div>
    </div>
);

export const SidebarShell = ({ tenantTitle, tenantSubtitle, navItems, activeKey, onNav, footer, testId = "sidebar" }) => (
    <aside data-testid={testId} className="hidden lg:flex lg:col-span-2 flex-col border-r hairline sticky top-0 self-start h-screen">
        <div className="p-6 border-b hairline">
            <div className="flex items-center gap-3">
                {tenantTitle}
            </div>
            {tenantSubtitle && <div className="mt-3 text-[10px] uppercase tracking-ultra-wide text-zinc-600">{tenantSubtitle}</div>}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
            {navItems.map((it) => {
                const Icon = it.icon;
                const active = it.key === activeKey;
                return (
                    <button
                        key={it.key}
                        onClick={() => onNav?.(it.key)}
                        data-testid={`nav-${it.key}`}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${active ? "bg-white/5 text-white border-l-2 pl-[10px]" : "text-zinc-400 hover:text-white hover:bg-white/[0.03]"}`}
                        style={active ? { borderLeftColor: "var(--tenant-primary)" } : {}}
                    >
                        <span className="inline-flex items-center gap-2.5">
                            <Icon size={15} strokeWidth={1.6} />
                            {it.label}
                        </span>
                        {it.soon && <span className="text-[9px] uppercase tracking-ultra-wide text-zinc-600">Soon</span>}
                    </button>
                );
            })}
        </nav>
        {footer}
    </aside>
);

export const Modal = ({ open, onClose, title, children, footer, wide = false, testId = "modal" }) => {
    if (!open) return null;
    return (
        <div data-testid={testId} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className={`relative bg-[#0a0a0a] border hairline w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto`}>
                <div className="px-6 py-4 border-b hairline flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Floodlight</div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white text-sm" data-testid={`${testId}-close`} aria-label="Close">×</button>
                </div>
                <div className="px-6 py-6">
                    <h3 className="font-display font-extrabold text-2xl tracking-tight mb-5">{title}</h3>
                    {children}
                </div>
                {footer && <div className="px-6 py-4 border-t hairline flex items-center justify-end gap-2">{footer}</div>}
            </div>
        </div>
    );
};

export const Field = ({ label, hint, children, span = 6 }) => (
    <div className={`col-span-12 md:col-span-${span}`}>
        <label className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 block mb-2">{label}</label>
        {children}
        {hint && <div className="text-[10px] text-zinc-600 mt-1.5">{hint}</div>}
    </div>
);

export const TextInput = ({ value, onChange, placeholder = "", type = "text", testId, ...rest }) => (
    <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border hairline px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-white/30 outline-none transition-colors"
        data-testid={testId}
        {...rest}
    />
);

export const Select = ({ value, onChange, options, testId }) => (
    <select
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full bg-[#0a0a0a] border hairline px-3 py-2.5 text-sm text-white focus:border-white/30 outline-none transition-colors"
        data-testid={testId}
    >
        {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ))}
    </select>
);

export const PrimaryButton = ({ children, tenantTheme = false, ...rest }) => (
    <button
        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold tracking-tight hover:brightness-110 transition-all disabled:opacity-50"
        style={tenantTheme
            ? { background: "var(--tenant-primary)", color: "var(--tenant-primary-foreground)" }
            : { background: "var(--sb-amber)", color: "#000000" }}
        {...rest}
    >
        {children}
    </button>
);

export const GhostButton = ({ children, ...rest }) => (
    <button className="inline-flex items-center justify-center gap-2 border hairline text-white px-5 py-2.5 text-sm hover:bg-white/5 transition-all" {...rest}>{children}</button>
);

export const FooterStrip = ({ tenant }) => (
    <div className="text-xs text-zinc-600 pt-12 pb-8 flex items-center justify-between border-t hairline">
        <span className="inline-flex items-center gap-2">
            <SoubourMark size={14} />
            <span className="font-editorial italic">powered by Soubor Applications Ltd</span>
        </span>
        <span>{tenant?.subdomain || "floodlight.club"}{tenant ? ` · tenant ${tenant.id}` : ""}</span>
    </div>
);

export { SouburWordmark };
